import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import { useSelector, useDispatch } from 'react-redux'
import { useIntl } from 'react-intl'
import { useParams, Link } from 'react-router-dom'
import { useForm, useWatch } from 'react-hook-form'

import { Grid, TableRow, TableCell } from '@mui/material'
import { SettingsIcon } from 'assets/icons'
import LoadingSpinnerGreen from 'components/ui/LoadingSpinnerGreen'
import ClubModalForm from 'components/form/ClubModalForm'
import RadioButtonGroup from 'components/form/RadioButtonGroup'
import TransferCourseToClub from 'components/form/TransferCourseToClub'
import SimpleTable from 'components/ui/SimpleTable'
import LinkButton from 'components/ui/LinkButton'
import RowMenu from 'components/ui/RowMenu'
import SuccessMessage from 'components/ui/SuccessMessage'

import { ClientRoute } from 'routes/client'
import { selectClub } from 'store/club/slice'
import { getClub, getClubCourses } from 'store/club/thunks'
import { selectCourse, clearDuplicatedCourse } from 'store/course/slice'
import { deactivateCourse, reactivateCourse, duplicateCourse } from 'store/course/thunks'

interface ParamTypes extends ParamTypesBase {
  id: string
}

export default function ClubCourses(): JSX.Element {
  const { id: paramId } = useParams<ParamTypes>()
  const id = paramId || ''
  const dispatch = useDispatch()
  const intl = useIntl()
  const [currentId, setCurrentId] = useState<string>('')
  const [rowMenu, setRowMenu] = useState<number | null>(null)
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const [clubModalOpen, setClubModalOpen] = useState<boolean>(false)

  const form = useForm()
  const { control, reset } = form
  const watch = useWatch({ control })
  const { club, status, coursesStatus } = useSelector(selectClub)
  const { saveStatus: courseSaveStatus, duplicatedCourse } = useSelector(selectCourse)
  const isLinkedToTmOrganization = club.tmOrganizationIds && club.tmOrganizationIds.length > 0

  useEffect(() => {
    if ((id && status === 'idle') || (id && currentId !== id)) {
      setCurrentId(id)
      dispatch(getClub(parseInt(id)))
      dispatch(getClubCourses(parseInt(id)))
    }
  }, [id, currentId, status, dispatch])

  useEffect(() => {
    reset({
      official: 'true',
      active: 'true',
    })
  }, [reset])

  const getFilteredCourses = () => {
    let courses = club.courses || []
    const official = getFilterValue(watch['official'])
    const active = getFilterValue(watch['active'])

    if (typeof official === 'boolean') {
      courses = courses.filter((course: Course) => course.isOfficial === official)
    }

    if (typeof active === 'boolean') {
      courses = courses.filter((course: Course) => course.isActive === active)
    }

    return courses
  }

  const getFilterValue = (value: string | null) => {
    if (!value) {
      return null
    }
    return value === 'true'
  }

  const columns: TableColumn[] = [
    {
      id: 'courseId',
      label: intl.formatMessage({ id: 'strings.courseId' }),
    },
    {
      id: 'course',
      label: intl.formatMessage({ id: 'strings.course' }),
    },
    {
      id: 'holes',
      label: intl.formatMessage({ id: 'strings.holes' }),
    },
    {
      id: 'par',
      label: intl.formatMessage({ id: 'strings.par' }),
    },
    {
      id: 'status',
      label: intl.formatMessage({ id: 'strings.status' }),
    },
    {
      id: 'active',
      label: intl.formatMessage({ id: 'strings.active' }),
    },
    {
      id: 'gps',
      label: intl.formatMessage({ id: 'strings.gps' }),
    },
    {
      id: 'playedRounds',
      label: intl.formatMessage({ id: 'strings.playedRounds' }),
    },
    {
      id: 'edit',
      label: '',
    },
    {
      id: 'provider',
      label: intl.formatMessage({ id: 'strings.providerMapping' }),
    },
  ]

  const handleRowMenuOpen = (event: any, id: number) => {
    setRowMenu(id)
    setAnchorEl(event.currentTarget)
  }

  const handleRowMenuClose = () => {
    setRowMenu(null)
    setAnchorEl(null)
  }

  const toggleClubModal = () => {
    setClubModalOpen(!clubModalOpen)
  }

  const submitDuplicateCourse = (id: number) => {
    dispatch(duplicateCourse(id))
  }

  const submitReactivation = (id: number) => {
    dispatch(reactivateCourse(id))
  }

  const submitDeactivation = (id: number) => {
    dispatch(deactivateCourse(id))
  }

  const getCourseStatusOfficial = (course: Course) => {
    return course.isOfficial
      ? intl.formatMessage({ id: 'strings.official' })
      : intl.formatMessage({ id: 'strings.unofficial' })
  }

  const getCourseStatusActive = (course: Course) => {
    return course.isActive ? intl.formatMessage({ id: 'strings.yes' }) : intl.formatMessage({ id: 'strings.no' })
  }

  const getCourseStatusGps = (course: Course) => {
    return course.gps ? intl.formatMessage({ id: 'strings.yes' }) : intl.formatMessage({ id: 'strings.no' })
  }

  const getReOrDeactivateElement = (course: Course) => {
    if (!course.isActive) {
      return <div onClick={() => submitReactivation(course.id)}>{intl.formatMessage({ id: 'strings.reactivate' })}</div>
    }
    const className = course.isOfficial ? 'disabled' : ''
    return (
      <div className={className} onClick={() => submitDeactivation(course.id)}>
        {intl.formatMessage({ id: 'strings.deactivate' })}
      </div>
    )
  }

  return (
    <>
      {clubModalOpen && (
        <ClubModalForm open={clubModalOpen} toggle={toggleClubModal} clubId={club.id} hideCoursesButton />
      )}

      <PageContainer>
        <SectionContainer>
          <SectionTitle>
            {intl.formatMessage({ id: 'strings.coursesOf' })} {club.name}
            {club.provider && <span>({club.provider.abbreviation})</span>}
            {status === 'loading' && <LoadingSpinnerGreen />}
          </SectionTitle>

          <Grid container spacing={6}>
            {isLinkedToTmOrganization && (
              <OrganizationSection item xs={2}>
                <OrganizationTitle>{intl.formatMessage({ id: 'strings.tmOrganizationId' })}</OrganizationTitle>
                <OrganizationIds>
                  {club.tmOrganizationIds &&
                    club.tmOrganizationIds.map((id: number) => (
                      <>
                        {id}
                        <br />
                      </>
                    ))}
                </OrganizationIds>
              </OrganizationSection>
            )}
            <GridSection item xs={isLinkedToTmOrganization ? 3 : 5}>
              <FilterForm>
                <div>{intl.formatMessage({ id: 'strings.status' })}</div>
                <div>
                  <RadioButtonGroup
                    name="official"
                    form={form}
                    elements={[
                      { label: intl.formatMessage({ id: 'strings.official' }), value: 'true', preSelected: true },
                      { label: intl.formatMessage({ id: 'strings.unofficial' }), value: 'false' },
                    ]}
                  />
                </div>
                <div>
                  <RadioButtonGroup
                    name="active"
                    form={form}
                    elements={[
                      { label: intl.formatMessage({ id: 'strings.active' }), value: 'true', preSelected: true },
                      { label: intl.formatMessage({ id: 'strings.deactivated' }), value: 'false' },
                    ]}
                  />
                </div>
              </FilterForm>
            </GridSection>
            <GridSection item xs={2}>
              <FilterForm>
                <OrganizationTitle>
                  {intl.formatMessage({ id: 'strings.providerClubId' })} :
                  <OrganizationIds>{club.providerClubMap?.providerClubId}</OrganizationIds>
                </OrganizationTitle>
              </FilterForm>
            </GridSection>
            <GridSection item xs={3}>
              <TransferCourseToClub />
            </GridSection>
            <GridSection item xs={2}>
              <ActionButtons>
                <ActionButton onClick={toggleClubModal}>{intl.formatMessage({ id: 'strings.editClub' })}</ActionButton>
                <StyledLinkButton
                  to={ClientRoute.ADMIN_ADD_NEW_CLUB_COURSE(parseInt(id))}
                  text={intl.formatMessage({ id: 'strings.addNewCourse' })}
                />
              </ActionButtons>
            </GridSection>
            {club.id > 0 && !club.timeZone && (
              <ValidTimeZoneDiv>
                <ValidTimeZone>{intl.formatMessage({ id: 'strings.validTimeZone' })}</ValidTimeZone>
              </ValidTimeZoneDiv>
            )}
          </Grid>
        </SectionContainer>
        <SectionContainer>
          {coursesStatus === 'loading' && (
            <LoadingSpinnerContainer>
              <LoadingSpinnerGreen size={48} />
            </LoadingSpinnerContainer>
          )}

          <SimpleTable columns={columns}>
            {getFilteredCourses().map((course, i) => (
              <TableRow hover key={i}>
                <TableCell>{course.id}</TableCell>
                <TableCell>
                  <CourseLink to={ClientRoute.ADMIN_CLUB_COURSE(club.id, course.id)}>{course.name}</CourseLink>
                </TableCell>
                <TableCell>{course.holeCount}</TableCell>
                <TableCell>{course.par}</TableCell>
                <TableCell>{getCourseStatusOfficial(course)}</TableCell>
                <TableCell>{getCourseStatusActive(course)}</TableCell>
                <TableCell>{getCourseStatusGps(course)}</TableCell>
                <TableCell>{course.roundCount}</TableCell>
                <TableCell>
                  <ActionMenuIcon onClick={(e) => handleRowMenuOpen(e, course.id)} />
                  <RowMenu open={course.id === rowMenu} anchorEl={anchorEl} handleClose={handleRowMenuClose}>
                    {courseSaveStatus === 'loading' ? (
                      <LoadingSpinnerGreen />
                    ) : (
                      <>
                        <Link to={ClientRoute.ADMIN_CLUB_COURSE(club.id, course.id)}>
                          {intl.formatMessage({ id: 'strings.modify' })}
                        </Link>
                        <div onClick={() => submitDuplicateCourse(course.id)}>
                          {intl.formatMessage({ id: 'strings.duplicate' })}
                        </div>
                        {getReOrDeactivateElement(course)}
                        <div className="disabled">{intl.formatMessage({ id: 'strings.remove' })}</div>
                      </>
                    )}
                  </RowMenu>
                </TableCell>
                <TableCell>{course.provider?.abbreviation || '-'}</TableCell>
              </TableRow>
            ))}
          </SimpleTable>
        </SectionContainer>

        {courseSaveStatus === 'succeeded' && duplicatedCourse !== null && (
          <SuccessMessage handleClose={() => dispatch(clearDuplicatedCourse())} duration={10000}>
            <>
              {intl.formatMessage({ id: 'strings.courseDuplicatedSuccessfully' })}
              <ViewCourseLink to={ClientRoute.ADMIN_CLUB_COURSE(duplicatedCourse.club?.id || 0, duplicatedCourse.id)}>
                {intl.formatMessage({ id: 'strings.viewCourse' })}
              </ViewCourseLink>
            </>
          </SuccessMessage>
        )}
      </PageContainer>
    </>
  )
}

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`

const SectionContainer = styled.div`
  background-color: ${(props) => props.theme.customPalette.light};
  padding: 30px;
  margin: 10px 0;
`

const SectionTitle = styled.h2`
  font-style: italic;
  font-weight: 800;
  font-size: 20px;
  line-height: 27px;
  color: ${(props) => props.theme.customPalette.darkGray2};
  margin-bottom: 25px;

  & > span {
    margin-left: 10px;
    font-size: 0.75em;
  }
`

const CourseLink = styled(Link)`
  color: ${(props) => props.theme.palette.primary.main};
  text-decoration: none;
`

const ActionMenuIcon = styled(SettingsIcon)`
  width: 20px;
  height: 20px;
  cursor: pointer;
`

const FilterForm = styled.form`
  display: flex;
  flex-wrap: wrap;
  & > div {
    width: 50%;

    @media (max-width: 1280px) {
      width: 100%;
    }

    &:first-of-type {
      width: 100%;
      color: ${(props) => props.theme.palette.primary.main};
      margin-bottom: 10px;
    }
  }
`

const GridSection = styled(Grid)`
  display: flex;
  justify-content: flex-start;
  align-items: flex-start;
`

const ActionButtons = styled.div`
  display: flex;
  flex-direction: column;

  & > * {
    margin: 5px 0;
  }
`

const ActionButton = styled.button`
  height: 30px;
  background-color: transparent;
  color: ${(props) => props.theme.palette.primary.main};
  border: 2px solid;
  border-color: ${(props) => props.theme.palette.primary.main};
  border-radius: 5px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 700;

  &:not(.disabled):hover {
    opacity: 0.85;
  }

  &.disabled {
    background-color: ${(props) => props.theme.customPalette.mediumGray};
    border-color: ${(props) => props.theme.customPalette.mediumGray};
    color: ${(props) => props.theme.customPalette.light};
    cursor: unset;
  }
`

const StyledLinkButton = styled(LinkButton)`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 30px;
  background-color: transparent;
  color: ${(props) => props.theme.palette.primary.main};
  border: 2px solid;
  border-color: ${(props) => props.theme.palette.primary.main};
  border-radius: 5px;
  font-weight: 700;
  font-size: 13px;
  text-decoration: none;

  &:not(.disabled):hover {
    background-color: transparent;
    opacity: 0.85;
  }
`

const LoadingSpinnerContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  padding: 0 0 20px;
`

const ViewCourseLink = styled(Link)`
  color: ${(props) => props.theme.customPalette.light};
  font-weight: 700;
  text-decoration: none;
  display: block;
  margin-top: 20px;

  &:visited {
    color: ${(props) => props.theme.customPalette.light};
  }

  &:hover {
    opacity: 0.75;
  }
`

const OrganizationSection = styled(GridSection)`
  flex-direction: column;
`

const OrganizationTitle = styled.div`
  width: 100%;
  color: ${(props) => props.theme.palette.primary.main};
  margin-bottom: 10px;
`

const OrganizationIds = styled.div`
  font-size: 16px;
  line-height: 24px;
  display: flex;
  flex-direction: column;
  width: 100%;
  color: #404040;
`
const ValidTimeZoneDiv = styled.div`
  margin-left: 20px;
  margin-bottom: 20px;
`

const ValidTimeZone = styled.span`
  padding: 5px;
  background-color: red;
  color: white;
`
